/**
 * @file exit-mixin.ts
 *
 * Everything about leaving the app on purpose without losing work: the window
 * close intercepts, the beforeunload veto, the two-phase unsaved-warning modal
 * and the button clicks that drive it (Home, transfer, save-before-exit,
 * confirm/cancel), the exit to the storefront, and the `pendingAction`
 * follow-through after a save. See docs/as-app.md, "Exit guards" and
 * "pendingAction state machine".
 *
 * Host differences (a webview where window.close() is a no-op, a cancelable
 * close request, a beforeunload veto the host honours) come in through the
 * `window` section of features/host/host.ts. Nothing here knows which host it is
 * running in.
 *
 * Requires: ModalMixinMembers, ProjectMixinMembers + Internals, BusyMixinMembers
 * Exposes: navigatingAway, redirectToStorefront
 */
import { isAppBusy } from './busy-mixin.ts';
import { getHost, CLOSE_REQUESTED_EVENT } from '../../../features/host/host.ts';
import { isSaveTrackable } from '../../../features/host/fileSave.ts';
import { fullAppSuiteRootURL, suitePageUrl } from '../../../data/constants.ts';
import { devlog } from '../../../util/utils.ts';
import type { AsModalSaveProject } from '../../modals/as-modal-save-project.ts';
import { type Constructor } from './mixin-types-utils.ts';
import type { ModalMixinMembers } from './modal-mixin.ts';
import type { ProjectMixinMembers, ProjectMixinInternals } from './project-mixin.ts';
import type { BusyMixinMembers } from './busy-mixin.ts';
import type { TransferMixinInternals } from './transfer-mixin.ts';
import { asConfigurator } from '../../../util/singletons.ts';
import { sendEv } from '../../../data/events.ts';

/** How long an exit prompt has to have been on screen before another press of
 *  the window's close button counts as a deliberate second press rather than
 *  the tail of a double-click. */
const CLOSE_PROMPT_REPRESS_MS = 500;

/** How long a close waits for an in-flight configure/export before giving up
 *  and cancelling the stuck request. */
const CLOSE_BUSY_WAIT_MS = 30000;

/** Surface added by ExitMixin. */
export interface ExitMixinMembers {
  /**
   * Set once the user has deliberately started leaving the app (Home / storefront
   * exit, app-to-app transfer). Both exit guards stand down while it's set: the
   * point of those guards is to catch an *accidental* departure, and this is the
   * opposite of that. Without it, the busy branch of the beforeunload veto
   * silently blocks every redirect out of a stuck page — see redirectToApp's
   * root-cause note.
   */
  navigatingAway: boolean;
  /** Leave for the storefront. Shared with the terms modal's Cancel. */
  redirectToStorefront(): void;
}

export const ExitMixin = <T extends Constructor<HTMLElement & ModalMixinMembers & ProjectMixinMembers & ProjectMixinInternals & TransferMixinInternals & BusyMixinMembers>>(Base: T) =>
  class ExitMixin extends Base {
    navigatingAway = false;

    /** Latched while a quit is in progress, so the close intercepts let it through
     *  instead of prompting again. */
    _closing = false;

    /**
     * When the window-close path last put a prompt on screen (waiting spinner or
     * unsaved warning), or 0 while no such prompt is up. A second press of the
     * host's close button while it's showing means "I meant it" and quits outright.
     */
    _closePromptAt = 0;

    async connectedCallback() {
      await super.connectedCallback?.();
      // Any route out of the overlay (confirm, cancel, Escape) disarms the
      // second-press force-quit, so it can only ever fire while the prompt it
      // belongs to is actually on screen.
      this.modalOverlay?.addEventListener('as-modal-closed', this._onOverlayClosed);
      window.addEventListener('as-close-app', this._onCloseApp);
      window.addEventListener(CLOSE_REQUESTED_EVENT, this._onCloseRequested);
      window.addEventListener('beforeunload', this._onBeforeUnload);
      window.addEventListener('as-button-click', this._handleExitButtonClick as EventListener);
    }

    disconnectedCallback() {
      this.modalOverlay?.removeEventListener('as-modal-closed', this._onOverlayClosed);
      window.removeEventListener('as-close-app', this._onCloseApp);
      window.removeEventListener(CLOSE_REQUESTED_EVENT, this._onCloseRequested);
      window.removeEventListener('beforeunload', this._onBeforeUnload);
      window.removeEventListener('as-button-click', this._handleExitButtonClick as EventListener);
      super.disconnectedCallback?.();
    }

    /**
     * Actually quit, the host's way if it has one (see host.ts), else
     * window.close(). `_closing` latches so the close intercepts let the ensuing
     * close pass through instead of re-prompting.
     */
    _quit(): void {
      this._closing = true;
      const close = () => window.close();
      const host = getHost()?.window;
      host?.quit ? host.quit(close) : close();
      // A webview's window.close() can be a no-op and the host may decline to
      // tear us down; if we're still alive a moment later, un-latch so the next
      // close attempt is intercepted again instead of silently passing through.
      // A permanently-stuck latch is why the unsaved warning only appeared
      // sporadically — once any non-closing quit ran, every subsequent close
      // request bailed at the `_closing` guard.
      setTimeout(() => { this._closing = false; }, 1000);
    }

    _onCloseApp = (event: Event): void => {
      if ((event as CustomEvent).detail?.save) {
        this.pendingAction = 'save-and-close';
        sendEv(this, 'as-save-session', { bubbles: false });
      } else {
        this._quit();
      }
    };

    /**
     * Primary window-close intercept, for a host that asks before closing (see
     * host.ts). We cancel the close and, when there are unsaved changes,
     * show the two-phase unsaved-warning modal (which quits via `_quit()` once
     * the user confirms); with nothing to save we quit straight away.
     *
     * If a configure round-trip or export is in flight we hold the close first,
     * show a "finishing up" view in the exit modal, and wait for the app to go
     * idle before deciding — closing mid-configure abandons the Paramate request
     * and can drop the last edit's ground-truth sync.
     */
    _onCloseRequested = async (event: Event): Promise<void> => {
      if (this._closing) return;
      event.preventDefault();      // keep the window open, we decide below

      // Second press while our own exit prompt is still up: the user has seen the
      // question and pressed close again, so quit without waiting or asking twice.
      // Otherwise a busy app just re-shows the spinner and an unsaved one just
      // re-opens the same warning, and pressing X looks broken. The short delay
      // keeps a double-click on the window's X from discarding unsaved work.
      if (this._closePromptAt && Date.now() - this._closePromptAt > CLOSE_PROMPT_REPRESS_MS) {
        this._quit();
        return;
      }

      if (this.isSaving) return;            // a save is mid-flight — stay

      if (isAppBusy()) {
        this._showExitWaiting();
        this._closePromptAt = Date.now();
        // On timeout, abort whatever's stuck so we don't hold the window open indefinitely.
        await this.waitForIdle(CLOSE_BUSY_WAIT_MS, () => {
          asConfigurator()?.configurator?.cancelLastRequest?.();
        });
        this.modalOverlay?.querySelector('#unsaved-warning')?.removeAttribute('waiting');
        // Re-check: a save may have kicked off while we waited.
        if (this._closing || this.isSaving) return;
        // Dismiss the waiting spinner before deciding — quitting closes the
        // overlay outright; the unsaved path reopens it in phase 1.
        this.modalOverlay?.close();
      }

      if (!this.unsavedChanges) { this._quit(); return; }
      this._openExitWarning('close');
      this._closePromptAt = Date.now();
    };

    /**
     * Fallback close intercept for a host that fires `beforeunload` but not its
     * own close request (when the close request fires, cancelling it means the
     * unload never happens and this never runs). Only for hosts that honour the
     * veto quietly (`vetoesUnload`), so a plain browser doesn't pop its native
     * "Leave site?" dialog on top of our modal.
     *
     * `beforeunload` is synchronous and cannot await, so an in-flight
     * configure/export can only be *blocked* here — cancel the close and let the
     * user retry once idle, at which point the close-request path handles it.
     */
    _onBeforeUnload = (event: BeforeUnloadEvent): void => {
      if (this._closing || this.navigatingAway) return;   // already committed to leaving
      if (!getHost()?.window?.vetoesUnload) return;
      if (!this.unsavedChanges && !isAppBusy()) return;
      event.preventDefault();
      event.returnValue = 'unsaved-changes';  // truthy value cancels the close in the host
    };

    /** Disarms the second-press force-quit — see `_closePromptAt`. */
    _onOverlayClosed = (): void => { this._closePromptAt = 0; };

    /**
     * Show the exit modal's "finishing up" waiting view. Skipped when the overlay
     * is already showing the export-progress modal, which reports its own progress
     * — no point covering it with a generic spinner.
     */
    _showExitWaiting(): void {
      if (this.modalOverlay?.hasAttribute('show') &&
          this.modalOverlay.getAttribute('visible-child') === 'export-progress') {
        return;
      }
      this.openModal('unsaved-warning', 'as-modal-unsaved-warning', { waiting: '' });
      const el = this.modalOverlay?.querySelector('#unsaved-warning');
      el?.removeAttribute('saved');
      el?.removeAttribute('mode');
    }

    /**
     * Open the unsaved-warning modal in its first ("unsaved") phase. `mode` picks
     * the verb (close / home / new / transfer); `saved` is cleared so a reused
     * element never reopens in the second phase from a prior interaction.
     */
    _openExitWarning(mode: 'close' | 'home' | 'new' | 'transfer'): void {
      this.openModal('unsaved-warning', 'as-modal-unsaved-warning', { mode });
      const el = this.modalOverlay?.querySelector('#unsaved-warning');
      el?.removeAttribute('saved');
      el?.removeAttribute('waiting');  // never let a stale waiting phase mask phase-1
    }

    /** The verb the unsaved-warning modal is currently open for. */
    _exitWarningMode(): string | null | undefined {
      return this.modalOverlay?.querySelector('#unsaved-warning')?.getAttribute('mode');
    }

    /**
     * Carry out the exit the unsaved-warning modal was opened for. Shared by the
     * as-intrn-confirm-exit-app button (phase-1 discard / phase-2 already-saved) and the
     * one-step save-and-exit that follows a successful trackable save, so a saved
     * exit doesn't require a second click. `mode` is the modal's verb: home
     * redirects to the storefront, new starts a fresh project, close quits,
     * transfer resumes the mesh-transfer flow that was paused for this warning.
     */
    _performExit(mode: string | null | undefined): void {
      this.pendingAction = null;
      this.modalOverlay?.close();

      // Discard tracking so the ensuing navigation/close isn't intercepted
      // by the beforeunload handler as a second "unsaved" warning.
      this.unsavedChanges = false;

      if (mode === 'home') {
        this.redirectToStorefront();
      } else if (mode === 'new') {
        this._createAndSetNewProject();
      } else if (mode === 'close') {
        this._quit();
      } else if (mode === 'transfer') {
        this._prepareTransferMesh();
      }
    }

    redirectToStorefront(): void {
      devlog('[as-app] redirectToStorefront -> navigating to', fullAppSuiteRootURL);
      // Anchor click rather than `window.location.href` so Exit-to-storefront also
      // works in an embedded webview (see navigateViaAnchor). Deferred to a fresh
      // task: the discard path calls this synchronously inside the button's click
      // dispatch, and an anchor click nested in that dispatch (right after the
      // dialog closes) is dropped by the webview. The save path only worked because
      // its `await` already escaped the dispatch — this gives the discard path the
      // same clean task. redirectToApp also covers the (rare) case where the click
      // is silently dropped: a "Redirecting…" spinner appears after 2s instead of
      // leaving the app looking stuck on a closed modal.
      this._redirectToApp(suitePageUrl(fullAppSuiteRootURL), false, 'Continue to home', 'exit_to_host');
    }

    /** The button clicks that start, confirm or cancel an exit. Runs alongside
     *  as-app's own as-button-click handler; the two handle disjoint names. */
    _handleExitButtonClick = async (event: CustomEvent): Promise<void> => {
      switch (event.detail.buttonName) {
        case 'transfer-mesh':
          // Same unsaved-changes gate as Home: transferring navigates away from
          // this app just as much as exiting to the storefront does, so it needs
          // the same "save before leaving?" prompt instead of silently discarding
          // (or, in a host with an unload veto, being silently blocked by the
          // beforeunload guard — see redirectToApp's root-cause note).
          if (this.unsavedChanges) {
            this.pendingAction = 'transfer';
            this._openExitWarning('transfer');
          } else {
            this._prepareTransferMesh();
          }
          break;
        case 'home':
          // Lean on unsavedChanges as the single source of truth — independent of
          // project mode (apps set this flag directly or via as-input-change).
          if (this.unsavedChanges) {
            this.pendingAction = 'home';
            this._openExitWarning('home');
          } else {
            this.redirectToStorefront();
          }
          break;
        case 'as-intrn-cancel-exit-app':
          // Stay put. Only originates from the unsaved-warning modal.
          this.pendingAction = null;
          this.modalOverlay?.close();
          break;
        case 'as-intrn-save-before-exit': {
          // Whether the save's completion can fold the exit into one step
          // depends on the environment's save provider (isSaveTrackable).
          const saved = await this._saveCurrentProject();

          if (isSaveTrackable()) {
            // Trackable provider (a native dialog reports its result): finish in
            // one step. A cancelled/unconfirmed save keeps us in phase 1 so no
            // work is lost; a successful save exits immediately — no redundant
            // "saved, leave when ready" second click. See fileSave.ts (isSaveTrackable).
            if (saved) this._performExit(this._exitWarningMode());
            break;
          }
          // Untrackable (browser <a download>): advance to phase 2 and let the
          // user choose when to leave.
          this.modalOverlay?.querySelector('#unsaved-warning')?.setAttribute('saved', '');
          break;
        }
        case 'as-intrn-confirm-exit-app':
          // Proceed with the exit the unsaved-warning modal was opened for. Same
          // action in both phases: phase 1 discards unsaved work, phase 2 has
          // already saved it. Driven by the modal's `mode` so it stays independent
          // of the pendingAction save-and-close machine (the GrabCAD-success modal).
          this._performExit(this._exitWarningMode());
          break;
        case 'confirm-save-project': {
          const saveModalEl = this.modalOverlay?.querySelector('#save-project') as AsModalSaveProject | null;
          const projectName = saveModalEl?.getValue() || 'Untitled';
          const saved = await this._saveCurrentProject(projectName);
          this._closeSaveProjectModal();
          // Only follow through with the pending exit on a real save. A cancelled
          // trackable save (see fileSave.ts) aborts it so no work is lost to a
          // dismissed Save dialog; a browser <a download> reports success.
          const action = saved ? this.pendingAction : null;
          this.pendingAction = null;
          if (action === 'new-session') this._createAndSetNewProject();
          else if (action === 'home') this.redirectToStorefront();
          else if (action === 'save-and-close') this._quit();
          else if (action === 'transfer') this._prepareTransferMesh();
          break;
        }
        case 'cancel-save-project':
          this._closeSaveProjectModal();
          this.pendingAction = null;
          break;
      }
    };
  };
